/**
 * Admin Service — cost management (admin-only; the backend gates every call).
 */
import { callFunction } from '../utils/apiClient';

export const ADMIN_EMAIL = 'xuwei@wisetree.ai';

export interface BudgetConfig {
  monthly_budget_usd: number;
  per_user_monthly_usd: number;
  hard_stop: boolean;
  paused: boolean;
  alert_thresholds: number[];
  per_user_overrides?: Record<string, number>;
}

export interface UsageTotals { tokens: number; cost_usd: number; requests: number; byCategory?: Record<string, number> }

export interface UserUsage extends UsageTotals { id: string; uid: string; email: string | null }

export interface CostSummary {
  month: string;
  config: BudgetConfig;
  totals: UsageTotals;
  topUsers: UserUsage[];
  budgetUsedPct: number;
}

export const getAdminStatus = () =>
  callFunction<{ isAdmin: boolean }>('getAdminStatus', {}).then((r) => r.isAdmin);

export const getCostSummary = () => callFunction<CostSummary>('getCostSummary', {});

/** Per-call AI cost breakdown from the ai_spend ledger — totals + splits by category, LLM tier, and day. */
export interface AiSpendBreakdown {
  days: number;
  totals: { requests: number; tokens: number; costUsd: number };
  byCategory: Record<string, number>;
  byTier: Record<string, number>;
  byDay: Array<{ day: string; costUsd: number; requests: number }>;
  scanned: number;
  capped: boolean;
}
export const getAiSpend = (days: 7 | 30 | 90 = 30) =>
  callFunction<AiSpendBreakdown>('getAiSpend', { days });

/** Product-usage engagement breakdown from the telemetry_events spine — event totals, top events, distinct
 *  users, and a daily series over the window. */
export interface TelemetryBreakdown {
  days: number;
  totals: { events: number; users: number };
  byEvent: Array<{ event: string; count: number }>;
  byDay: Array<{ day: string; count: number }>;
  scanned: number;
  capped: boolean;
}
export const getTelemetryAnalytics = (days: 7 | 30 = 7) =>
  callFunction<TelemetryBreakdown>('getTelemetryAnalytics', { days });

/** Top users over a rolling window (7/30/90 days) by estimated AI cost (chat + media). Email masked. */
export interface TopUserRow { uid: string; email: string; requests: number; tokens: number; costUsd: number; lastActive: string | null }
export const getTopUsers = (days: 7 | 30 | 90 = 30, limit = 200) =>
  callFunction<{ days: number; users: TopUserRow[] }>('getTopUsers', { days, limit });

export interface AgentPopularity { profileId: string; name: string; replyCount: number; lastActive: string | null }
/** Most popular / busiest agents platform-wide (admin-only). rangeDays 7|30|90 ranks by that
 *  window; 0 = all-time. */
export const getAgentPopularity = (limit = 50, rangeDays: 0 | 7 | 30 | 90 = 0) =>
  callFunction<{ agents: AgentPopularity[]; total: number; rangeDays: number }>('getAgentPopularity', { limit, rangeDays });

export interface RiskyUser {
  uid: string; email: string | null; riskCount: number; lastRisk: string | null;
  lastRiskAt: string | null; disabled: boolean; recent: { risk: string; excerpt: string }[];
}
/** Users who tripped the content-safety guardrail most, for admin review (admin-only). */
export const getRiskyUsers = (limit = 50) =>
  callFunction<{ users: RiskyUser[] }>('getRiskyUsers', { limit }).then((r) => r.users);
/** Disable or re-enable a user account after manual review (admin-only). */
export const setUserDisabled = (targetUid: string, disabled: boolean) =>
  callFunction<{ targetUid: string; disabled: boolean }>('setUserDisabled', { targetUid, disabled });

/** Per-user usage breakdown (admin-only) — AI spend, wallet, creations, conversations. PII masked. */
export interface UserUsageBreakdown {
  uid: string; email: string; plan: string; month: string;
  spend: { requests: number; tokens: number; costUsd: number };
  wallet: { wp: number; wpSpent: number };
  creations: { image: number; video: number; audio: number; total: number };
  conversations: number | null;
}
export const getUserUsageBreakdown = (targetUid: string) =>
  callFunction<UserUsageBreakdown>('getUserUsageBreakdown', { targetUid });

/** Live error groups (admin-only) — frontend + backend errors, grouped by signature. */
export interface ErrorGroupRow {
  signature: string; source: 'frontend' | 'backend'; route: string; status: number;
  count: number; users: number; lastSeen: string | null; firstSeen?: string | null; sample: string;
  /** Detail from the most-recent event in the group (so the admin sees file/line + the error object). */
  kind?: string | null; name?: string | null; code?: string | null; stack?: string | null; url?: string | null; userAgent?: string | null;
  /** The release tag the error was logged under (e.g. "v0.1.36"), so issues fixed in a newer version can be filtered out. */
  version?: string | null;
}
export const getErrorGroups = (days: 1 | 7 | 30 = 7, source?: 'frontend' | 'backend', version?: string) =>
  callFunction<{ groups: ErrorGroupRow[]; days: number; scanned: number; versions: string[] }>('getErrorGroups', { days, source, version });

/** AI triage of the window's errors → a prioritized, copy-paste-ready fix list (markdown). */
export const analyzeErrors = (days: 1 | 7 | 30 = 7, source?: 'frontend' | 'backend', version?: string) =>
  callFunction<{ analysis: string; groupCount: number; scanned: number }>('analyzeErrors', { days, source, version });

// ── User directory + deep-dive (admin-only) ──
export interface AdminUserRow { uid: string; email: string | null; name: string | null; createdAt: string | null; lastSignIn?: string | null }
export interface UserProfileAgent { agentId: string; name: string; persona: string; level: number; points: number }
export interface UserProfile {
  user: { uid: string; email: string | null; name: string | null; createdAt: string | null; lastSignIn?: string | null; plan: string };
  wallet: { plan: string; wp: number } | null;
  topAgents: UserProfileAgent[];
  interests: string[];
  activity: { days: number; requests: number; tokens: number; costUsd: number; distinctAgents: number; lastActive: string | null };
  recentEvents: SupervisionRow[];
}
/** Search users by email prefix, or browse the most recent signups when the query is empty. */
export const adminListUsers = (search?: string, limit = 25) =>
  callFunction<{ users: AdminUserRow[]; search: string | null }>('adminListUsers', { search, limit });
/** Deep-dive one user (by uid or email): top agents, interests, 30-day activity, wallet. */
export const getUserProfile = (params: { uid?: string; email?: string }) =>
  callFunction<UserProfile>('getUserProfile', params);

// ── Email template editor (admin-only) — branding + default copy for the weekly-summary email ──
export interface EmailTemplateOverrides {
  brandName?: string; subjectPrefix?: string; ctaLabel?: string; footerNote?: string;
  introDefault?: string; teaserDefault?: string; updated_at?: string;
}
export const adminGetEmailTemplate = () => callFunction<EmailTemplateOverrides>('adminGetEmailTemplate', {});
export const adminSetEmailTemplate = (overrides: EmailTemplateOverrides) =>
  callFunction<EmailTemplateOverrides>('adminSetEmailTemplate', { overrides });
/** Render the email with the given (unsaved) overrides for a live preview — no send. */
export const adminPreviewEmailTemplate = (overrides?: EmailTemplateOverrides) =>
  callFunction<{ subject: string; html: string; text: string }>('adminPreviewEmailTemplate', { overrides });
/** Send a test email to an address (default the admin) — a no-op until the mail endpoint is configured. */
export const adminSendTestEmail = (to?: string) =>
  callFunction<{ to: string; subject: string; sent: boolean; skipped?: boolean; error?: string; configured?: boolean; hasToken?: boolean; hasAccount?: boolean }>('adminSendTestEmail', { to });

// Fast-forward the REAL next weekly email for one user (by email or uid) — built from their live activity + book,
// sent with the real copywriter. Respects the user's subscribe pref + suppression (reports a clean skip instead).
export const adminSendRealEmailNow = (params: { email?: string; uid?: string; force?: boolean }) =>
  callFunction<{ uid: string; type: string; sent: boolean; skipped?: boolean; reason?: string; retryInSec?: number; error?: string; configured?: boolean }>('adminSendRealEmailNow', params);

// ── Email recipient allowlist (admin-only) — staged-rollout gate for WHO receives the weekly broadcast ──
export type AllowlistMode = 'all' | 'allowlist';
export interface EmailAllowlist { mode: AllowlistMode; emails: string[]; updated_at?: string }
export const adminGetEmailAllowlist = () => callFunction<EmailAllowlist>('adminGetEmailAllowlist', {});
export const adminSetEmailAllowlist = (cfg: { mode: AllowlistMode; emails: string[] }) =>
  callFunction<EmailAllowlist>('adminSetEmailAllowlist', cfg);

// ── Supervision: traffic analysis + prompt/response oversight (admin-only, PII masked) ──

/** A single supervised chat turn — prompt + agent response, masked. */
export interface SupervisionRow {
  createdAt: string | null; agentName: string; tier: string;
  prompt: string; response: string; tokens: number; costUsd: number;
  conversationId: string; email: string;
}
/** One day of traffic (requests / distinct users / tokens / $). `day: 'total'` marks the totals row. */
export interface TrafficBucket { day: string; requests: number; users: number; tokens: number; costUsd: number }

/** Per-day traffic over the last 7 or 30 days + window totals. */
export const getTrafficAnalytics = (days: 7 | 30 = 7) =>
  callFunction<{ days: number; buckets: TrafficBucket[]; totals: TrafficBucket; scanned: number }>('getTrafficAnalytics', { days });

/** Recent prompts + responses across all users (masked) over the window. */
export const getSupervisionFeed = (days: 7 | 30 = 7, limit = 100) =>
  callFunction<{ days: number; events: SupervisionRow[] }>('getSupervisionFeed', { days, limit });

/** Dive into one user by email: their usage behaviour + recent prompts/responses (masked). */
export interface UserSupervision {
  user: { uid: string; email: string; plan: string; wp: number | null };
  days: number; totals: TrafficBucket; dailyActivity: TrafficBucket[]; events: SupervisionRow[];
}
export const getUserSupervision = (email: string, days: 7 | 30 = 7, limit = 100) =>
  callFunction<UserSupervision>('getUserSupervision', { email, days, limit });

export const setBudgetConfig = (patch: Partial<BudgetConfig>) =>
  callFunction<{ config: BudgetConfig }>('setBudgetConfig', patch).then((r) => r.config);

export const setUserCap = (targetUid: string, capUsd: number) =>
  callFunction<{ config: BudgetConfig }>('setUserCap', { targetUid, capUsd }).then((r) => r.config);

export const pauseLLM = (paused: boolean) =>
  callFunction<{ paused: boolean }>('pauseLLM', { paused }).then((r) => r.paused);

export const resetUserUsage = (targetUid: string) =>
  callFunction<{ reset: boolean }>('resetUserUsage', { targetUid });

/** A catalog agent as the admin edits it (merged code + overrides). */
export interface CatalogAgent {
  id: string;
  name: string;
  persona: string;
  description: string;
  avatar_icon: string;
  avatar_image: string | null;
  backstory: string;
  system_prompt: string;
  model_tier: string;
  temperature: number;
  reasoning_effort?: 'low' | 'medium' | 'high';
  domain: string | null;
  capabilities: string[];
  isDefault: boolean;
  is_code: boolean;
  /** Personal hobby (human touch + a KB domain the agent can chat about). */
  hobby?: string;
  /** Nested persona card — round-tripped WHOLE so editing one field never wipes the others. */
  identity?: { personality?: string; gender?: 'female' | 'male' | 'neutral'; appearance?: string; style?: string; skills?: string[]; [k: string]: any };
  /** Voice this agent reads messages with — '' / undefined = auto-pick by gender. */
  voice_id?: string | null;
  voice_quality?: 'standard' | 'premium';
}

export const adminListCatalog = () =>
  callFunction<{ agents: CatalogAgent[] }>('adminListCatalog', {}).then((r) => r.agents || []);

export const adminSaveAgent = (agent: Partial<CatalogAgent> & { id: string }) =>
  callFunction<{ id: string }>('adminSaveAgent', agent);

export const adminDeleteAgent = (id: string, disabled = true) =>
  callFunction<{ id: string; disabled: boolean }>('adminDeleteAgent', { id, disabled });

export const adminGenerateAgentAvatar = (id: string, prompt: string) =>
  callFunction<{ id: string; avatar_image: string }>('adminGenerateAgentAvatar', { id, prompt });

/** Upload an image file (base64) as an agent's avatar in the CURRENT environment. */
export const adminSetAgentAvatar = (id: string, imageBase64: string) =>
  callFunction<{ id: string; avatar_image: string }>('adminSetAgentAvatar', { id, imageBase64 });

export interface GalleryPostRow {
  id: string; index: number; scene: string; aspect: string; image_path: string | null;
  caption: string; locked: boolean; like_count?: number; comment_count?: number; views?: number; style?: string;
}

/** List an agent's existing gallery posts + the selectable style keys (admin). */
export const adminListGallery = (id: string) =>
  callFunction<{ id: string; posts: GalleryPostRow[]; styles: string[] }>('adminListGallery', { id });

/** Generate + append `count` fresh social-gallery photos for an agent in a chosen STYLE (admin). */
export const adminGenerateGallery = (id: string, style: string, count: number) =>
  callFunction<{ id: string; style: string; requested: number; generated: number; created: GalleryPostRow[]; results: { index: number; scene: string; ok: boolean; error?: string }[] }>(
    'adminGenerateGallery', { id, style, count });

// ── Agent voice picker (admin) ──
export interface VoiceOption { id: string; label: string }
export const adminListVoices = () =>
  callFunction<{ voices: { female: VoiceOption[]; male: VoiceOption[] } }>('adminListVoices', {}).then((r) => r.voices);
/** Preview a voice on a sample phrase → a data-URL MP3 to play inline. */
export const adminPreviewVoice = (voiceId: string, quality: 'standard' | 'premium') =>
  callFunction<{ voiceId: string; audio: string }>('adminPreviewVoice', { voiceId, quality });

// --- Admin knowledge base LAYER (domain modules mapped to agents) ---
export type KbScope = 'agent' | 'domain' | 'all';
export interface KbSourceRow { source: string; bytes: number; chunks: number; added_at: string }
export interface KbUsageRow { scopeId: string; bytes: number; cap: number; remaining: number; sources: KbSourceRow[] }
export interface KbTargetAgent { id: string; name: string; domain: string | null; backend: string; knowledge_domains: string[] }
export interface KbDomainRow { id: string; usage: KbUsageRow; agents: { id: string; name: string }[] }

/** List injectable targets (roster with their mapped domains + the domain taxonomy + distillation tiers). */
export const adminKbTargets = () =>
  callFunction<{ agents: KbTargetAgent[]; domains: string[]; tiers: string[] }>('adminKbTargets', {});

/** The KB LAYER: every domain module with usage + the agents mapped to it (shared/overlapping). */
export const adminKbDomains = () =>
  callFunction<{ domains: KbDomainRow[] }>('adminKbDomains', {}).then((r) => r.domains);

/** Assign/replace an agent's specialised KB domains (mapping into the domain layer). */
export const adminKbAssignAgentDomains = (agentId: string, domains: string[]) =>
  callFunction<{ agentId: string; knowledge_domains: string[] }>('adminKbAssignAgentDomains', { agentId, domains });

export const adminKbUsage = (scope: KbScope, id?: string) =>
  callFunction<{ usage: KbUsageRow }>('adminKbUsage', { scope, id }).then((r) => r.usage);

/** Ingest text/html/url into a target scope; `model` (a tier) optionally distills it first. */
export const adminKbIngest = (
  scope: KbScope, id: string | undefined, kind: 'text' | 'html' | 'url', content: string, model?: string, source?: string,
) => callFunction<{ usage: KbUsageRow }>('adminKbIngest', { scope, id, kind, content, model, source }).then((r) => r.usage);

/** Ingest a document (base64) into a target scope; `model` optionally distills it first. */
export const adminKbIngestDoc = (
  scope: KbScope, id: string | undefined, base64: string, mime: string, name: string, model?: string,
) => callFunction<{ usage: KbUsageRow }>('adminKbIngest', { scope, id, kind: 'document', content: base64, mime, name, source: name, model }).then((r) => r.usage);

export const adminKbRemoveSource = (scope: KbScope, id: string | undefined, source: string) =>
  callFunction<{ usage: KbUsageRow }>('adminKbRemoveSource', { scope, id, source }).then((r) => r.usage);

export const adminKbClear = (scope: KbScope, id?: string) =>
  callFunction<{ usage: KbUsageRow }>('adminKbClear', { scope, id }).then((r) => r.usage);

/** Turn on retrieval for the targeted agents (flip 'none' agents to 'simple'). Returns count switched. */
export const adminKbEnableRetrieval = (scope: KbScope, id?: string) =>
  callFunction<{ switched: number }>('adminKbEnableRetrieval', { scope, id }).then((r) => r.switched);

// --- SELF-LEARNING LIVING LAYER: the human-gated compounding candidate an admin reviews before it goes live ---

export type KbCandidateStatus = 'none' | 'accumulating' | 'pending_review';
export type KbLearnVerdict = 'improved' | 'equal' | 'regressed' | 'unassessed';
export interface KbLearningConfig { windowMonths: number | null; cadenceDays: number; autoApprove: boolean; lastLearnedAt?: string | null; lastPublishedAt?: string | null }
export interface KbCandidateRow {
  domain: string; status: KbCandidateStatus; variant: 'a' | 'b' | null;
  cycles: number; ingested: number; rejected: number; redundant: number;
  verdict: KbLearnVerdict; depthDelta: number; breadthDelta: number;
  /** Absolute benchmark scores behind the deltas: LIVE (current) vs PENDING (the candidate). 0 until benchmarked. */
  depthLive?: number; depthPending?: number; breadthLive?: number; breadthPending?: number;
  reasons: string[]; samples: { source: string; preview: string }[];
  started_at: string | null; updated_at: string | null; config: KbLearningConfig;
}

/** Every learning domain's pending-candidate evidence + its window config (pending first) — the review board. */
export const adminKbCandidates = () =>
  callFunction<{ candidates: KbCandidateRow[] }>('adminKbCandidates', {}).then((r) => r.candidates || []);

/** Full-detail review of what a pending candidate will publish — the new notes (full text, grouped by source),
 *  for the admin to read BEFORE approving. Empty when there's no pending candidate. */
export interface KbCandidateNote { source: string; text: string }
export const adminKbReviewCandidate = (domain: string) =>
  callFunction<{ domain: string; status: string; notes: KbCandidateNote[]; total: number }>('adminKbReviewCandidate', { domain });

/** Start an async "learn now" for one domain (real research, ~30s) — returns a job id to poll. */
export const adminKbLearnDomain = (domain: string) =>
  callFunction<{ jobId: string; domain: string; status: string }>('adminKbLearnDomain', { domain });

export interface KbLearnJob { id: string; status: 'running' | 'done' | 'error'; domain: string; ingested?: number; verdict?: KbLearnVerdict; error?: string }
/** Poll a "learn now" job (running / done + counts / error). */
export const adminKbLearnStatus = (jobId: string) =>
  callFunction<{ job: KbLearnJob }>('adminKbLearnStatus', { jobId }).then((r) => r.job);

/** Publish a domain's pending candidate (atomic blue-green flip; prior live kept as a version). */
export const adminKbApproveCandidate = (domain: string) =>
  callFunction<{ published: boolean; variant?: string; snapshotVersion?: number | null }>('adminKbApproveCandidate', { domain });

/** Discard a domain's pending candidate (reset from live; nothing published). */
export const adminKbRejectCandidate = (domain: string) =>
  callFunction<{ discarded: boolean }>('adminKbRejectCandidate', { domain });

/** Set a domain's learning window: FIFO retention months, refresh cadence days, opt-in low-risk auto-approve. */
export const adminKbSetLearningWindow = (domain: string, patch: { windowMonths?: number; cadenceDays?: number; autoApprove?: boolean }) =>
  callFunction<{ domain: string; config: Partial<KbLearningConfig> }>('adminKbSetLearningWindow', { domain, ...patch });

export type KbSourceRegKind = 'news' | 'research' | 'tech' | 'books' | 'blogs' | 'rss';
export interface KbRegisteredSource { url: string; label?: string; kind: KbSourceRegKind }

/** The reputable-source registry the researcher prefers for a domain. */
export const adminKbGetSources = (domain: string) =>
  callFunction<{ domain: string; sources: KbRegisteredSource[]; kinds: KbSourceRegKind[] }>('adminKbGetSources', { domain });

/** Replace a domain's reputable-source registry (server validates + caps). */
export const adminKbSetSources = (domain: string, sources: KbRegisteredSource[]) =>
  callFunction<{ domain: string; sources: KbRegisteredSource[] }>('adminKbSetSources', { domain, sources }).then((r) => r.sources);

export interface KbArchiveRow { path: string; ts: string; version: number | null; sources: number; bytes: number }
/** A domain's retained version archives (newest first). */
export const adminKbArchives = (domain: string) =>
  callFunction<{ domain: string; archives: KbArchiveRow[] }>('adminKbArchives', { domain }).then((r) => r.archives);

// --- KB LEARNING SELF-TEST: admin-triggered, server-side validation of the whole loop on real infra ---
export interface KbSelfTestStep { name: string; ok: boolean; detail: string; ms: number }
export interface KbSelfTestRun {
  id: string; status: 'running' | 'done' | 'error'; domain: string;
  ok?: boolean; steps?: KbSelfTestStep[]; triggered_by?: string; error?: string;
}
/** Start the self-test (real research/publish/archive on an isolated scope + probes). Returns a run id to poll. */
export const adminKbRunSelfTest = (domain?: string) =>
  callFunction<{ runId: string; domain: string; status: string }>('adminKbRunSelfTest', domain ? { domain } : {});
/** Poll one self-test run (per-step report). */
export const adminKbSelfTestStatus = (runId: string) =>
  callFunction<{ run: KbSelfTestRun }>('adminKbSelfTestStatus', { runId }).then((r) => r.run);
/** Recent self-test runs (newest first). */
export const adminKbSelfTestHistory = () =>
  callFunction<{ runs: KbSelfTestRun[] }>('adminKbSelfTestHistory', {}).then((r) => r.runs || []);

// --- KB SOURCE INSPECTOR: the raw material (files/urls/html scrapes/llm output) before embedding ---

export type KbSourceKind = 'text' | 'html' | 'url' | 'document' | 'llm';
export interface KbRawSourceRow {
  source: string; kind: KbSourceKind; url?: string; mime?: string;
  chars: number; bytes: number; added_at: string; truncated: boolean; preview: string;
  distilled?: boolean; distilled_by?: string; original_kind?: KbSourceKind;
}
export interface KbRawSourceDoc extends KbRawSourceRow { text: string; original_text?: string; original_truncated?: boolean }

/** List retained RAW sources for a scope (provenance + preview, no full text). */
export const adminKbSources = (scope: KbScope, id?: string) =>
  callFunction<{ scope: string; sources: KbRawSourceRow[] }>('adminKbSources', { scope, id }).then((r) => r.sources);

/** Full raw text of one source — the original scrape + (if distilled) the LLM output that was embedded. */
export const adminKbSourceContent = (scope: KbScope, id: string | undefined, source: string) =>
  callFunction<{ source: KbRawSourceDoc }>('adminKbSourceContent', { scope, id, source }).then((r) => r.source);

// --- GUARDRAILS: the AI-gateway's LLM content-safety guardrail (token-costing) for THIS environment ---

export interface GuardrailsState { configured: boolean; enabled?: boolean; gateway?: string; message?: string }

/** Read whether the gateway's LLM guardrail is on (it runs a model per call → costs tokens). */
export const getGatewayGuardrails = () =>
  callFunction<GuardrailsState>('getGatewayGuardrails', {});

/** Turn the gateway's LLM guardrail on/off for this environment. */
export const setGatewayGuardrails = (enabled: boolean) =>
  callFunction<GuardrailsState>('setGatewayGuardrails', { enabled });

// --- REQUEST TRACES: per-turn agent-pipeline execution traces (who/what/retrieval/model/reply/errors) ---

export interface TraceSpan {
  step: string; type: string; status: 'ok' | 'error' | 'skip';
  at_ms: number; duration_ms?: number; agent?: string;
  detail?: Record<string, unknown>; error?: { message: string; code?: string };
}
export interface TraceRow {
  id: string; created_iso: string; uid: string | null; email: string | null; conversation_id: string | null;
  request_text: string; status: string; duration_ms: number;
  retrieval_mode: string; retrieval_chunks: number; model_tiers: string[];
  integrations: string[]; responders: { id: string; name: string }[]; reply_count: number;
}
export interface TraceDetail extends TraceRow {
  request_chars: number; attachments: number; retrieval_scopes: string[];
  response_text: string; response_components: string[]; spans: TraceSpan[];
  error?: { step: string; message: string; code?: string };
}

/** Recent request traces within a day window, optionally filtered by user email or status. */
export const getRequestTraces = (days: 1 | 7 | 30 = 7, email?: string, status?: string) =>
  callFunction<{ traces: TraceRow[]; scanned: number; days: number }>('getRequestTraces', { days, email, status });

/** One full trace with its spans, for the detail drill-down. */
export const getRequestTrace = (id: string) =>
  callFunction<{ trace: TraceDetail }>('getRequestTrace', { id }).then((r) => r.trace);
